import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Chip,
  Avatar,
  AvatarGroup,
  Button,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import { IoTrashOutline, IoPencilOutline, IoArrowForward } from 'react-icons/io5';
import { FaChalkboardTeacher, FaUserGraduate } from 'react-icons/fa';
import { alpha } from '@mui/material';
import palette from 'src/theme/palette';
import { CohortWithDetails } from 'src/types/cohort.types';
import { useAuth } from 'src/context/auth/AuthContext';

interface CohortCardProps {
  cohort: CohortWithDetails;
  baseRoute?: string;
  onEditCohort?: (cohort: CohortWithDetails) => void;
  onDeleteCohort?: (cohortId: string, cohortName: string) => void;
  onAssignTeachers?: (cohort: CohortWithDetails) => void;
  onAssignStudents?: (cohort: CohortWithDetails) => void;
  onClick?: () => void;
}

export function CohortCard({
  cohort,
  baseRoute,
  onEditCohort,
  onDeleteCohort,
  onAssignTeachers,
  onAssignStudents,
  onClick,
}: CohortCardProps) {
  const navigate = useNavigate();
  const { hasPermission, getBaseRoute } = useAuth();

  const canViewTeachers = hasPermission('teachers.view_all');
  const teacherBaseRoute = getBaseRoute('/view-teacher');
  const targetBaseRoute = baseRoute || getBaseRoute('/cohorts');

  const totalBalance = (cohort.students || []).reduce((sum, s) => sum + (s.balance || 0), 0);
  const studentCount = cohort.students?.length ?? cohort.studentCount ?? 0;
  const teacherCount = cohort.teachers?.length ?? 0;
  const avgBalance = studentCount > 0 ? Math.round(totalBalance / studentCount) : 0;

  const handleCardClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`${targetBaseRoute}/${cohort.id}`);
    }
  };

  return (
    <Card
      elevation={0}
      onClick={handleCardClick}
      sx={{
        borderRadius: 2.5,
        border: '1px solid #E2E8F0',
        bgcolor: '#FFFFFF',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          borderColor: '#CBD5E0',
          boxShadow: '0 6px 20px rgba(15, 23, 42, 0.07)',
          transform: 'translateY(-2px)',
          '& .cohort-arrow-btn': {
            bgcolor: alpha(palette.primary.main, 0.12),
            color: palette.primary.main,
            transform: 'translateX(2px)',
          },
        },
      }}
    >
      <CardContent
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          '&:last-child': { pb: 2 },
        }}
      >
        {/* Top Header Row */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.25 }}>
          <Box sx={{ flex: 1, pr: 0.75 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.25 }}>
              <Typography variant="subtitle1" fontWeight={800} color="#0F172A" lineHeight={1.2}>
                {cohort.name}
              </Typography>
              <Chip
                label={cohort.className || 'Grade Section'}
                size="small"
                sx={{
                  backgroundColor: '#F1F5F9',
                  color: '#334155',
                  fontWeight: 600,
                  height: 20,
                  fontSize: '0.6875rem',
                  borderRadius: 1,
                  border: '1px solid #E2E8F0',
                }}
              />
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                fontSize: '0.78125rem',
                minHeight: '2.2em',
                lineHeight: 1.35,
              }}
            >
              {cohort.description || 'No section description provided.'}
            </Typography>
          </Box>

          {/* Quick Actions */}
          <Stack direction="row" spacing={0.25} alignItems="center" onClick={(e) => e.stopPropagation()}>
            {onEditCohort && (
              <Tooltip title="Edit Cohort Details">
                <IconButton
                  size="small"
                  onClick={() => onEditCohort(cohort)}
                  sx={{
                    p: 0.5,
                    color: '#64748B',
                    borderRadius: 1.25,
                    '&:hover': { bgcolor: '#F1F5F9', color: '#0F172A' },
                  }}
                >
                  <IoPencilOutline size={15} />
                </IconButton>
              </Tooltip>
            )}

            {onDeleteCohort && (
              <Tooltip title="Delete Cohort">
                <IconButton
                  size="small"
                  onClick={() => onDeleteCohort(cohort.id, cohort.name)}
                  sx={{
                    p: 0.5,
                    color: '#EF4444',
                    borderRadius: 1.25,
                    '&:hover': { bgcolor: '#FEF2F2', color: '#DC2626' },
                  }}
                >
                  <IoTrashOutline size={15} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {/* Vital Metrics Strip */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
            mb: 1.5,
          }}
        >
          {/* Scholars & Vault Box */}
          <Box
            sx={{
              p: 1.25,
              bgcolor: '#F8FAFC',
              borderRadius: 1.75,
              border: '1px solid #F1F5F9',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: 1,
                  bgcolor: alpha(palette.primary.main, 0.08),
                  color: palette.primary.main,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <FaUserGraduate size={11} />
              </Box>
              <Typography variant="caption" fontWeight={700} color="#64748B" fontSize="0.65rem" letterSpacing={0.3}>
                SCHOLARS
              </Typography>
            </Stack>

            <Typography variant="subtitle2" fontWeight={800} color="#0F172A" lineHeight={1.1}>
              {studentCount}{' '}
              <Typography component="span" variant="caption" color="text.secondary" fontWeight={500} fontSize="0.7rem">
                enrolled
              </Typography>
            </Typography>
            <Typography variant="caption" color={palette.primary.main} fontWeight={700} fontSize="0.7rem" display="block" mt={0.15}>
              ${totalBalance.toLocaleString()} vault
            </Typography>
          </Box>

          {/* Educators Box */}
          <Box
            sx={{
              p: 1.25,
              bgcolor: '#F8FAFC',
              borderRadius: 1.75,
              border: '1px solid #F1F5F9',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: 1,
                  bgcolor: alpha(palette.secondary.main, 0.12),
                  color: '#C48F12',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <FaChalkboardTeacher size={11} />
              </Box>
              <Typography variant="caption" fontWeight={700} color="#64748B" fontSize="0.65rem" letterSpacing={0.3}>
                EDUCATORS
              </Typography>
            </Stack>

            <Typography variant="subtitle2" fontWeight={800} color="#0F172A" lineHeight={1.1}>
              {teacherCount}{' '}
              <Typography component="span" variant="caption" color="text.secondary" fontWeight={500} fontSize="0.7rem">
                staff
              </Typography>
            </Typography>
            <Typography variant="caption" color="#475569" fontWeight={600} fontSize="0.7rem" display="block" mt={0.15}>
              ~${avgBalance.toLocaleString()} avg
            </Typography>
          </Box>
        </Box>

        {/* Assigned Teachers Avatar Stack */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5, mt: 'auto', minHeight: 26 }}
        >
          <Box>
            {cohort.teachers && cohort.teachers.length > 0 ? (
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 22, height: 22, fontSize: '0.6875rem' } }}>
                  {cohort.teachers.map((t) => (
                    <Tooltip key={t.id} title={`${t.firstname} ${t.lastname} (${t.email})${canViewTeachers ? ' - View Profile' : ''}`}>
                      <Avatar
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canViewTeachers) {
                            navigate(`${teacherBaseRoute}/${t.teacherId || t.id}`);
                          }
                        }}
                        sx={{
                          bgcolor: alpha(palette.secondary.main, 0.15),
                          color: '#854D0E',
                          fontWeight: 700,
                          cursor: canViewTeachers ? 'pointer' : 'default',
                          '&:hover': { transform: canViewTeachers ? 'scale(1.1)' : 'none' },
                        }}
                      >
                        {t.firstname?.[0] || 'T'}
                      </Avatar>
                    </Tooltip>
                  ))}
                </AvatarGroup>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  fontSize="0.7rem"
                  onClick={(e) => {
                    if (canViewTeachers && cohort.teachers && cohort.teachers[0]) {
                      e.stopPropagation();
                      navigate(`${teacherBaseRoute}/${cohort.teachers[0].teacherId || cohort.teachers[0].id}`);
                    }
                  }}
                  sx={{ '&:hover': canViewTeachers ? { color: '#0F172A', textDecoration: 'underline' } : undefined, cursor: canViewTeachers ? 'pointer' : 'default' }}
                >
                  {cohort.teachers.map((t) => `${t.firstname} ${t.lastname?.[0] || ''}.`).slice(0, 2).join(', ')}
                  {cohort.teachers.length > 2 ? ` +${cohort.teachers.length - 2}` : ''}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary" fontStyle="italic" fontSize="0.7rem">
                No educators assigned
              </Typography>
            )}
          </Box>

          {onAssignTeachers && (
            <Button
              size="small"
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                onAssignTeachers(cohort);
              }}
              sx={{
                fontSize: '0.6875rem',
                fontWeight: 700,
                color: '#64748B',
                p: 0.25,
                minWidth: 'auto',
                '&:hover': { color: '#0F172A', bgcolor: 'transparent' },
              }}
            >
              Manage
            </Button>
          )}
        </Stack>

        <Divider sx={{ mb: 1.25, borderColor: '#F1F5F9' }} />

        {/* Bottom Action Footer */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary" fontWeight={600} fontSize="0.725rem">
            {studentCount} {studentCount === 1 ? 'scholar' : 'scholars'}
          </Typography>

          <Button
            size="small"
            className="cohort-arrow-btn"
            endIcon={<IoArrowForward size={13} />}
            sx={{
              fontWeight: 700,
              fontSize: '0.725rem',
              color: '#0F172A',
              bgcolor: '#F1F5F9',
              borderRadius: 1.5,
              px: 1.25,
              py: 0.35,
              transition: 'all 0.2s ease',
              textTransform: 'none',
            }}
          >
            Workspace
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
